/**
 * Per-hour and per-day view models for the time cursor. Everything the page shows for an hour —
 * the strings, in the person's units, through the app's own formatters — is built here once per
 * report and unit settings, so scrubbing is an array lookup and a text write, never a format.
 *
 * - Hours cover the whole hourly series the report has (48 – 240), starting at the hour the
 *   place's clock is in, which reads "Now" (the same rule as `buildHourlyItems`). The labels depend
 *   on that hour, so rebuild when the hour turns (the clock tick the hourly strip already uses).
 * - Days are the report's daily range, each with the cursor instant to jump to.
 * - A missing field is null — never "0", never a dash — so a view can leave its slot blank.
 * - Time zone, calendar day and the repeated hour of a fall-back day come from the place's zone
 *   through `lib/dayWindow.ts`; nothing here uses the device's zone or counts 24 hours in a day.
 */
import * as SunCalc from 'suncalc'
import type { ConditionCode, DayPoint, HourPoint, WeatherReport } from '../../shared/contract'
import { conditionLabel, iconSlug } from '../../shared/conditions'
import {
  compass,
  fmtDistance,
  fmtHour,
  fmtPercent,
  fmtPrecip,
  fmtPressure,
  fmtSnow,
  fmtTemp,
  fmtTime,
  fmtWeekday,
  fmtWind,
  localDate,
  temp,
  type Units,
} from '../../shared/units'
import { HOUR, zonedTime } from '../../engine/util'
import { dayAxis, scrubHour } from '../lib/dayWindow'
import { currentHourIndex, hourLabelTime } from '../lib/forecast'

/** Local wall-clock hour a picked day opens at: early afternoon, when the day's high is near. */
export const DAY_JUMP_HOUR = 14

export interface HourView {
  /** Position in `TimeModels.hours`. */
  index: number
  /** Start of the hour, epoch ms (the cursor's own unit). */
  t: number
  iso: string
  /** The hour the place's clock is in. */
  isNow: boolean
  /** Local calendar day of the hour (YYYY-MM-DD, the place's zone). */
  date: string
  /** Index into `TimeModels.days`, or -1 when the daily range does not cover this date. */
  dayIndex: number
  /** "Today", "Wed" — as the daily list names the day. */
  dayName: string
  /** The strip's label: "Now", "3 PM" / "15". */
  label: string
  /** The wall-clock hour, never "Now": "3 PM" / "15". */
  time: string
  /** "3 PM", or "1 AM EST" on the two repeated hours of a fall-back day. */
  timeFull: string
  /** "3:00 PM" / "15:00". */
  clock: string
  /** The cursor's readout: "Now", "Today 3 PM", "Wed 3 PM". */
  stamp: string
  isDay: boolean
  condition: ConditionCode
  conditionText: string
  /** Sprite slug (`<use href="#wx-…">`), day/night resolved. */
  icon: string
  /** "72°". */
  temp: string | null
  /** Temperature in the person's unit, unrounded; for placing a marker. */
  tempValue: number | null
  feels: string | null
  /** "12 mph". */
  wind: string | null
  /** "SW". */
  windDir: string | null
  /** Direction the wind blows from, degrees (a needle's angle). */
  windDeg: number | null
  gust: string | null
  humidity: string | null
  dewPoint: string | null
  /** The index, rounded: "6". */
  uv: string | null
  visibility: string | null
  pressure: string | null
  /** "40%". */
  chance: string | null
  chanceValue: number | null
  /** Liquid amount for the hour in the person's unit. */
  precip: string | null
  /** Snowfall for the hour; null unless the report has it. */
  snow: string | null
  cloud: string | null
  /** US AQI, rounded; null when the report has no hourly air quality. */
  aqi: string | null
  /** Sun elevation in degrees at the start of the hour (negative below the horizon). */
  sunAltitude: number | null
  /** The wire hour, for instruments that need SI values. */
  hour: HourPoint
}

export interface DayView {
  index: number
  date: string
  isToday: boolean
  /** "Today", "Wed". */
  name: string
  condition: ConditionCode
  conditionText: string
  icon: string
  high: string | null
  low: string | null
  highValue: number | null
  lowValue: number | null
  chance: string | null
  chanceValue: number | null
  /** Real hours in the day: 24, or 23 / 25 where clocks change. */
  hours: number
  /**
   * The cursor instant a pick of this day moves to: that day's early afternoon (14:00 local, the
   * hour that holds it). Null for today — "now". Past the end of the hourly series the instant
   * is still the day's afternoon; the cursor clamps it to the last hour (`hourIndex` is null).
   */
  jumpTo: number | null
  /** Index into `TimeModels.hours` of the hour `jumpTo` lands on; null when the series ends before it. */
  hourIndex: number | null
  day: DayPoint
}

export interface TimeModels {
  timeZone: string
  hours: readonly HourView[]
  days: readonly DayView[]
  /** First and last hour (epoch ms): the cursor's horizon. Null for a report without hours. */
  start: number | null
  end: number | null
  /** Index of the hour containing `instant`, clamped to the series. */
  indexAt(instant: number): number
  hourAt(instant: number): HourView | null
  /** The day an instant falls on, in the place's zone. */
  dayAt(instant: number): DayView | null
}

const orNull = <T>(v: number | null | undefined, f: (v: number) => T): T | null => (v == null || !Number.isFinite(v) ? null : f(v))

function sunAltitude(t: number, lat: number, lon: number): number | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
  // suncalc 2 reports the altitude in degrees (the engine's own sun_altitude_deg is this value).
  return Math.round(SunCalc.getPosition(new Date(t), lat, lon).altitude * 10) / 10
}

/** Build the models. Pure; `now` is the clock the "Now" hour is measured against. */
export function buildTimeModels(report: WeatherReport, units: Units, now: number = Date.now()): TimeModels {
  const tz = report.location.timezone
  const { latitude, longitude } = report.location
  const today = localDate(new Date(now).toISOString(), tz)
  const nowDate = new Date(now)

  const dayIndexByDate = new Map<string, number>()
  report.daily.forEach((d, i) => dayIndexByDate.set(d.date, i))

  const first = currentHourIndex(report.hourly, now)
  const series = report.hourly.slice(first)
  const times = series.map((h) => Date.parse(h.time))

  const hours: HourView[] = series.map((h, index) => {
    const t = times[index]
    const date = localDate(h.time, tz)
    const dayName = fmtWeekday(date, tz, nowDate)
    const labelTime = hourLabelTime(h.time, tz)
    const axis = dayAxis(date, tz)
    const isNow = index === 0
    const time = fmtHour(labelTime, tz, units.clock)
    const timeFull = scrubHour(axis, (t - axis.start) / HOUR, labelTime, units.clock)
    return {
      index,
      t,
      iso: h.time,
      isNow,
      date,
      dayIndex: dayIndexByDate.get(date) ?? -1,
      dayName,
      label: isNow ? 'Now' : time,
      time,
      timeFull,
      clock: fmtTime(labelTime, tz, units.clock),
      stamp: isNow ? 'Now' : `${dayName} ${timeFull}`,
      isDay: h.is_day,
      condition: h.condition,
      conditionText: conditionLabel(h.condition),
      icon: iconSlug(h.condition, h.is_day),
      temp: orNull(h.temperature_c, (v) => fmtTemp(v, units.temp)),
      tempValue: temp(h.temperature_c, units.temp),
      feels: orNull(h.apparent_c, (v) => fmtTemp(v, units.temp)),
      wind: orNull(h.wind_ms, (v) => fmtWind(v, units.wind)),
      windDir: orNull(h.wind_dir_deg, compass),
      windDeg: orNull(h.wind_dir_deg, (v) => v),
      gust: orNull(h.wind_gust_ms, (v) => fmtWind(v, units.wind)),
      humidity: orNull(h.humidity_pct, fmtPercent),
      dewPoint: orNull(h.dewpoint_c, (v) => fmtTemp(v, units.temp)),
      uv: orNull(h.uv_index, (v) => String(Math.round(v))),
      visibility: orNull(h.visibility_m, (v) => fmtDistance(v, units.distance)),
      pressure: orNull(h.pressure_hpa, (v) => fmtPressure(v, units.pressure)),
      chance: orNull(h.precip_probability, fmtPercent),
      chanceValue: orNull(h.precip_probability, (v) => v),
      precip: orNull(h.precip_mm, (v) => fmtPrecip(v, units.precip)),
      snow: orNull(h.snow_cm, (v) => fmtSnow(v, units.precip)),
      cloud: orNull(h.cloud_cover_pct, fmtPercent),
      aqi: orNull(h.aqi, (v) => String(Math.round(v))),
      sunAltitude: sunAltitude(t, latitude, longitude),
      hour: h,
    }
  })

  const start = hours.length ? hours[0].t : null
  const end = hours.length ? hours[hours.length - 1].t : null

  /** Index of the hour containing `instant`: an arithmetic guess, corrected for gaps in the series. */
  const indexAt = (instant: number): number => {
    const n = hours.length
    if (!n) return 0
    let i = Math.min(n - 1, Math.max(0, Math.floor((instant - hours[0].t) / HOUR)))
    while (i > 0 && hours[i].t > instant) i--
    while (i < n - 1 && hours[i + 1].t <= instant) i++
    return i
  }

  const days: DayView[] = report.daily.map((day, index) => {
    const isToday = day.date === today
    const jump = zonedTime(day.date, DAY_JUMP_HOUR, tz)
    const inSeries = !!hours.length && jump >= hours[0].t && jump < hours[hours.length - 1].t + HOUR
    const hourIndex = isToday ? 0 : inSeries ? indexAt(jump) : null
    return {
      index,
      date: day.date,
      isToday,
      name: fmtWeekday(day.date, tz, nowDate),
      condition: day.condition,
      conditionText: day.description || conditionLabel(day.condition),
      icon: iconSlug(day.condition, true),
      high: orNull(day.high_c, (v) => fmtTemp(v, units.temp)),
      low: orNull(day.low_c, (v) => fmtTemp(v, units.temp)),
      highValue: temp(day.high_c, units.temp),
      lowValue: temp(day.low_c, units.temp),
      chance: orNull(day.precip_probability, fmtPercent),
      chanceValue: orNull(day.precip_probability, (v) => v),
      hours: dayAxis(day.date, tz).length,
      jumpTo: isToday ? null : hourIndex != null ? hours[hourIndex].t : jump,
      hourIndex,
      day,
    }
  })
  const dayByDate = new Map(days.map((d) => [d.date, d]))

  return {
    timeZone: tz,
    hours,
    days,
    start,
    end,
    indexAt,
    hourAt: (instant) => (hours.length ? hours[indexAt(instant)] : null),
    dayAt: (instant) => dayByDate.get(localDate(new Date(instant).toISOString(), tz)) ?? null,
  }
}

const cache = new WeakMap<WeatherReport, { key: string; models: TimeModels }>()

/**
 * The models for a report in these units, built once: the same report, units and "Now" hour
 * return the same object. Rebuilds when the unit settings change or the clock moves to a new hour.
 */
export function getTimeModels(report: WeatherReport, units: Units, now: number = Date.now()): TimeModels {
  const key = `${units.temp}${units.wind}${units.precip}${units.pressure}${units.distance}${units.clock}|${currentHourIndex(report.hourly, now)}|${Math.floor(now / HOUR)}`
  const hit = cache.get(report)
  if (hit && hit.key === key) return hit.models
  const models = buildTimeModels(report, units, now)
  cache.set(report, { key, models })
  return models
}

